// src/pages/maincourante/MainCouranteDetails.js - DÉTAILS
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

const COLORS = {
  primary: '#0B3C5D',
  secondary: '#1E88C8',
  accent: '#4FC3F7',
  darkText: '#2E2E2E',
  lightBg: '#F5F7FA',
  white: '#FFFFFF',
  danger: '#f44336',
  success: '#4caf50',
  warning: '#ff9800',
  info: '#2196f3'
};

const MainCouranteDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [mainCourante, setMainCourante] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [canEdit, setCanEdit] = useState(false);

  useEffect(() => {
    const fetchDetails = async () => {
      setLoading(true);
      setError("");
      
      const token = localStorage.getItem("access_token");
      if (!token) {
        setError("Token d'authentification manquant.");
        setLoading(false);
        setTimeout(() => navigate("/login"), 2000);
        return;
      }
      
      try {
        const response = await axios.get(`${API_BASE_URL}/main_courante/get.php?id=${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        
        if (response.data?.success) {
          setMainCourante(response.data.data);
          setCanEdit(response.data.data.can_edit || false);
        } else {
          setError(response.data?.message || "Main courante non trouvée.");
        }
      } catch (err) {
        console.error("Erreur:", err);
        
        if (err.response?.status === 401) {
          setError("Session expirée. Veuillez vous reconnecter.");
          localStorage.clear();
          setTimeout(() => navigate("/login"), 2000);
        } else if (err.response?.status === 403) {
          setError("Accès interdit. Vous n'avez pas la permission de voir cette main courante.");
        } else if (err.response?.status === 404) {
          setError("Main courante non trouvée.");
        } else {
          setError("Erreur lors du chargement des détails.");
        }
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchDetails();
    }
  }, [id, navigate]);

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Non spécifié';
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-5">
          <div className="spinner-border" style={{ color: COLORS.primary }} role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
          <p className="mt-2" style={{ color: COLORS.darkText }}>Chargement des détails...</p>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="container py-5">
          <div className="text-center">
            <div className="mb-4">
              <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{ 
                width: '80px',
                height: '80px',
                backgroundColor: '#ffebee',
                color: COLORS.danger
              }}>
                <i className="bi bi-exclamation-triangle fs-1"></i>
              </div>
            </div>
            <h5 className="mb-3" style={{ color: COLORS.primary }}>{error}</h5>
            <button
              className="btn fw-semibold mt-3"
              onClick={() => navigate("/maincourante")}
              style={{
                color: COLORS.primary,
                borderColor: COLORS.primary,
                backgroundColor: 'transparent',
                borderRadius: '8px',
                padding: '10px 24px',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = COLORS.primary;
                e.target.style.color = COLORS.white;
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = COLORS.primary;
              }}
            >
              <i className="bi bi-arrow-left me-2"></i>
              Retour à la liste
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Main courante #{id}</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard">Tableau de bord</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/maincourante">Main courante</Link>
            </li>
            <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Détails</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="card" style={{ 
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          border: 'none'
        }}>
          <div className="card-body">
            {/* En-tête avec actions */}
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h5 className="card-title mb-1" style={{ 
                  color: COLORS.primary,
                  borderBottom: `2px solid ${COLORS.lightBg}`,
                  paddingBottom: '16px',
                  width: '100%'
                }}>
                  <div className="d-flex align-items-center">
                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-3" style={{ 
                      width: '36px',
                      height: '36px',
                      backgroundColor: '#E3F2FD',
                      color: COLORS.primary
                    }}>
                      <i className="bi bi-journal-text"></i>
                    </div>
                    Détails de la main courante
                  </div>
                </h5>
                <p className="text-muted mb-0" style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                  <i className="bi bi-info-circle me-1"></i>
                  Consultation complète des informations et observations reportées
                </p>
              </div>
              
              <div className="d-flex gap-2">
                <button
                  className="btn fw-semibold"
                  onClick={() => navigate("/maincourante")}
                  style={{
                    color: COLORS.primary,
                    borderColor: COLORS.primary,
                    backgroundColor: 'transparent',
                    borderRadius: '8px',
                    padding: '10px 20px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#E3F2FD';
                    e.target.style.borderColor = COLORS.secondary;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.borderColor = COLORS.primary;
                  }}
                >
                  <i className="bi bi-arrow-left me-2"></i>
                  Retour
                </button>
                
                {canEdit && (
                  <button
                    className="btn fw-semibold"
                    onClick={() => navigate(`/maincourante/edit/${id}`)}
                    style={{
                      color: COLORS.white,
                      backgroundColor: COLORS.warning,
                      borderColor: COLORS.warning,
                      borderRadius: '8px',
                      padding: '10px 20px',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#f57c00';
                      e.target.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = COLORS.warning;
                      e.target.style.transform = 'translateY(0)';
                    }}
                  >
                    <i className="bi bi-pencil me-2"></i>
                    Modifier
                  </button>
                )}
                
                <button
                  className="btn fw-semibold"
                  onClick={handlePrint}
                  style={{
                    color: COLORS.white,
                    backgroundColor: COLORS.secondary,
                    borderColor: COLORS.secondary,
                    borderRadius: '8px',
                    padding: '10px 20px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#1976d2';
                    e.target.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = COLORS.secondary;
                    e.target.style.transform = 'translateY(0)';
                  }}
                >
                  <i className="bi bi-printer me-2"></i>
                  Imprimer
                </button>
              </div>
            </div>

            {/* Informations principales */}
            <div className="row mb-4">
              <div className="col-md-6">
                <div className="card h-100" style={{ 
                  backgroundColor: '#E3F2FD',
                  border: 'none',
                  borderRadius: '8px'
                }}>
                  <div className="card-body">
                    <h6 className="card-subtitle mb-3" style={{ 
                      color: COLORS.primary,
                      fontWeight: '600',
                      fontSize: '0.9rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      <i className="bi bi-calendar-event me-2"></i>
                      Informations temporelles
                    </h6>
                    
                    <div className="row">
                      <div className="col-6">
                        <div className="mb-3">
                          <label className="form-label mb-1" style={{ 
                            color: '#6c757d',
                            fontSize: '0.85rem',
                            fontWeight: '500'
                          }}>
                            <i className="bi bi-calendar-plus me-1"></i>
                            Date de création
                          </label>
                          <div style={{ 
                            color: COLORS.darkText,
                            fontWeight: '500',
                            fontSize: '0.95rem'
                          }}>
                            {mainCourante.created_at_formatted}
                          </div>
                        </div>
                      </div>
                      
                      <div className="col-6">
                        <div className="mb-3">
                          <label className="form-label mb-1" style={{ 
                            color: '#6c757d',
                            fontSize: '0.85rem',
                            fontWeight: '500'
                          }}>
                            <i className="bi bi-calendar-check me-1"></i>
                            Dernière modification
                          </label>
                          <div style={{ 
                            color: COLORS.darkText,
                            fontWeight: '500',
                            fontSize: '0.95rem'
                          }}>
                            {mainCourante.updated_at_formatted || 'Non modifiée'}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-3">
                      <span className="badge fw-semibold me-2" style={{ 
                        backgroundColor: canEdit ? '#e8f5e9' : '#f5f5f5',
                        color: canEdit ? '#2e7d32' : '#757575',
                        padding: '6px 12px',
                        borderRadius: '6px',
                        fontSize: '0.85rem'
                      }}>
                        <i className={`bi ${canEdit ? 'bi-pencil-square' : 'bi-lock'} me-1`}></i>
                        {canEdit ? 'Modifiable' : 'Lecture seule'}
                      </span>
                      
                      {mainCourante.hours_since_creation !== undefined && (
                        <span className="badge fw-semibold" style={{ 
                          backgroundColor: COLORS.lightBg,
                          color: COLORS.darkText,
                          padding: '6px 12px',
                          borderRadius: '6px',
                          fontSize: '0.85rem'
                        }}>
                          <i className="bi bi-clock-history me-1"></i>
                          Créée il y a {mainCourante.hours_since_creation} h
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="col-md-6">
                <div className="card h-100" style={{ 
                  backgroundColor: '#E3F2FD',
                  border: 'none',
                  borderRadius: '8px'
                }}>
                  <div className="card-body">
                    <h6 className="card-subtitle mb-3" style={{ 
                      color: COLORS.primary,
                      fontWeight: '600',
                      fontSize: '0.9rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      <i className="bi bi-geo-alt me-2"></i>
                      Site concerné
                    </h6>
                    
                    <div className="mb-3">
                      <label className="form-label mb-1" style={{ 
                        color: '#6c757d',
                        fontSize: '0.85rem',
                        fontWeight: '500'
                      }}>
                        <i className="bi bi-building me-1"></i>
                        Nom du site
                      </label>
                      <div style={{ 
                        color: COLORS.primary,
                        fontWeight: '600',
                        fontSize: '1.1rem'
                      }}>
                        {mainCourante.site.nom}
                      </div>
                    </div>
                    
                    <div className="mb-3">
                      <label className="form-label mb-1" style={{ 
                        color: '#6c757d',
                        fontSize: '0.85rem',
                        fontWeight: '500'
                      }}>
                        <i className="bi bi-geo me-1"></i>
                        Adresse
                      </label>
                      <div style={{ 
                        color: COLORS.darkText,
                        fontSize: '0.95rem'
                      }}>
                        {mainCourante.site.adresse || 'Non spécifiée'}
                      </div>
                    </div>
                    
                    <div className="mt-3">
                      <span className="badge fw-semibold" style={{ 
                        backgroundColor: '#E3F2FD',
                        color: COLORS.primary,
                        padding: '6px 12px',
                        borderRadius: '6px',
                        fontSize: '0.85rem'
                      }}>
                        <i className="bi bi-tag me-1"></i>
                        
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Agent et motif */}
            <div className="row mb-4">
              <div className="col-md-6">
                <div className="card h-100" style={{ 
                  backgroundColor: '#E3F2FD',
                  border: 'none',
                  borderRadius: '8px'
                }}>
                  <div className="card-body">
                    <h6 className="card-subtitle mb-3" style={{ 
                      color: COLORS.primary,
                      fontWeight: '600',
                      fontSize: '0.9rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      <i className="bi bi-person-badge me-2"></i>
                      Agent responsable
                    </h6>
                    
                    <div className="d-flex align-items-center mb-3">
                      <div className="me-3">
                        <div className="text-white rounded-circle d-flex align-items-center justify-content-center fw-semibold"
                          style={{ 
                            width: '50px', 
                            height: '50px', 
                            fontSize: '18px',
                            backgroundColor: COLORS.primary,
                            boxShadow: '0 2px 4px rgba(11, 60, 93, 0.1)'
                          }}
                        >
                          {mainCourante.agent.nom_complet?.split(' ').map(n => n[0]).join('') || 'AG'}
                        </div>
                      </div>
                      <div>
                        <div style={{ 
                          color: COLORS.primary, 
                          fontWeight: '600',
                          fontSize: '1.1rem'
                        }}>
                          {mainCourante.agent.nom_complet}
                        </div>
                        <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                          Matricule: {mainCourante.agent.matricule}
                        </div>
                      </div>
                    </div>
                    
                    <div className="row">
                      <div className="col-6">
                        <div className="mb-3">
                          <label className="form-label mb-1" style={{ 
                            color: '#6c757d',
                            fontSize: '0.85rem',
                            fontWeight: '500'
                          }}>
                            <i className="bi bi-telephone me-1"></i>
                            Téléphone
                          </label>
                          <div style={{ 
                            color: COLORS.darkText,
                            fontSize: '0.95rem'
                          }}>
                            {mainCourante.agent.telephone || 'Non spécifié'}
                          </div>
                        </div>
                      </div>
                      
                     
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="col-md-6">
                <div className="card h-100" style={{ 
                  backgroundColor: '#E3F2FD',
                  border: 'none',
                  borderRadius: '8px'
                }}>
                  <div className="card-body">
                    <h6 className="card-subtitle mb-3" style={{ 
                      color: COLORS.primary,
                      fontWeight: '600',
                      fontSize: '0.9rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      <i className="bi bi-chat-text me-2"></i>
                      Motif et observations
                    </h6>
                    
                    <div className="mb-4">
                      <label className="form-label mb-2" style={{ 
                        color: '#6c757d',
                        fontSize: '0.85rem',
                        fontWeight: '500'
                      }}>
                        <i className="bi bi-tag me-1"></i>
                        Motif principal
                      </label>
                      <div className="p-3" style={{ 
                        backgroundColor: COLORS.white,
                        borderRadius: '6px',
                        border: '1px solid #e0e0e0'
                      }}>
                        <div style={{ 
                          color: COLORS.primary,
                          fontWeight: '600',
                          fontSize: '1.1rem'
                        }}>
                          {mainCourante.motif}
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <label className="form-label mb-2" style={{ 
                        color: '#6c757d',
                        fontSize: '0.85rem',
                        fontWeight: '500'
                      }}>
                        <i className="bi bi-card-text me-1"></i>
                        Description détaillée
                      </label>
                      <div className="p-3" style={{ 
                        backgroundColor: COLORS.white,
                        borderRadius: '6px',
                        border: '1px solid #e0e0e0',
                        minHeight: '100px'
                      }}>
                        <div style={{ 
                          color: COLORS.darkText,
                          fontSize: '0.95rem',
                          lineHeight: '1.6',
                          whiteSpace: 'pre-wrap'
                        }}>
                          {mainCourante.description || 'Aucune description supplémentaire.'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="mt-4 pt-4 border-top">
              <div className="d-flex justify-content-between">
                <div>
                  <button
                    className="btn fw-semibold me-2"
                    onClick={() => navigate("/maincourante")}
                    style={{
                      color: COLORS.primary,
                      borderColor: COLORS.primary,
                      backgroundColor: 'transparent',
                      borderRadius: '8px',
                      padding: '10px 24px',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#E3F2FD';
                      e.target.style.borderColor = COLORS.secondary;
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.borderColor = COLORS.primary;
                    }}
                  >
                    <i className="bi bi-arrow-left me-2"></i>
                    Retour à la liste
                  </button>
                </div>
                
                <div className="d-flex gap-2">
                  {canEdit && (
                    <button
                      className="btn fw-semibold"
                      onClick={() => navigate(`/maincourante/edit/${id}`)}
                      style={{
                        color: COLORS.white,
                        backgroundColor: COLORS.warning,
                        borderColor: COLORS.warning,
                        borderRadius: '8px',
                        padding: '10px 24px',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#f57c00';
                        e.target.style.transform = 'translateY(-2px)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = COLORS.warning;
                        e.target.style.transform = 'translateY(0)';
                      }}
                    >
                      <i className="bi bi-pencil me-2"></i>
                      Modifier cette main courante
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Styles d'impression */}
      <style>{`
        @media print {
          .sidebar,
          .header,
          .breadcrumb,
          .btn,
          nav,
          .pagetitle h1 {
            display: none !important;
          }
          
          .card {
            border: none !important;
            box-shadow: none !important;
          }
          
          .card-body {
            padding: 0 !important;
          }
          
          body {
            background-color: white !important;
            color: black !important;
          }
          
          .badge {
            background-color: #f5f5f5 !important;
            color: black !important;
            border: 1px solid #ccc !important;
          }
        }
        
        @media (max-width: 768px) {
          .d-flex.justify-content-between {
            flex-direction: column;
            gap: 16px;
          }
          
          .d-flex.justify-content-between > div {
            width: 100%;
          }
          
          .btn {
            width: 100%;
            margin-bottom: 8px;
          }
        }
      `}</style>
    </Layout>
  );
};

export default MainCouranteDetails;